import React, { useState, useEffect } from 'react';
import { Table, Button, Modal, Form, Input, Select, message, Space, Popconfirm, Row, Col, Divider, Upload, Radio, Image } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, UploadOutlined } from '@ant-design/icons';
import { fieldsAPI, modulesAPI, listItemsAPI, uploadAPI } from '../api/client';
import { FIELD_TYPES, API_BASE_URL } from '../utils/constants';

const { TextArea } = Input;

const FieldManager = () => {
  const [fields, setFields] = useState([]);
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [moduleFilter, setModuleFilter] = useState(null);
  const [form] = Form.useForm();
  const [listItems, setListItems] = useState([]);
  const [listItemModalVisible, setListItemModalVisible] = useState(false);
  const [editingListItem, setEditingListItem] = useState(null);
  const [listItemForm] = Form.useForm();
  const [currentFieldId, setCurrentFieldId] = useState(null);
  const [coverFileList, setCoverFileList] = useState([]);

  const resolveAssetUrl = (path) => {
    if (!path) return '';
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    return `${API_BASE_URL}${path}`;
  };

  // 加载模块列表
  const loadModules = async () => {
    try {
      const data = await modulesAPI.getAll({});
      setModules(data);
    } catch (error) {
      message.error('加载模块列表失败: ' + error.message);
    }
  };

  // 加载字段列表
  const loadFields = async () => {
    setLoading(true);
    try {
      const params = moduleFilter ? { module_id: moduleFilter } : {};
      const data = await fieldsAPI.getAll(params);
      setFields(data);
    } catch (error) {
      message.error('加载字段列表失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModules();
  }, []);

  useEffect(() => {
    loadFields();
  }, [moduleFilter]);

  // 监听字段类型变化，自动加载列表项
  const fieldType = Form.useWatch('field_type', form);
  useEffect(() => {
    if (modalVisible && fieldType === 'list' && currentFieldId) {
      loadListItems(currentFieldId);
    } else if (modalVisible && fieldType !== 'list') {
      setListItems([]);
    }
  }, [fieldType, modalVisible, currentFieldId]);

  // 加载列表项
  const loadListItems = async (fieldId) => {
    if (!fieldId) {
      setListItems([]);
      return;
    }
    try {
      const data = await listItemsAPI.getAll({ field_id: fieldId });
      setListItems(data);
    } catch (error) {
      message.error('加载列表项失败: ' + error.message);
    }
  };

  // 打开创建/编辑弹窗
  const openModal = async (field = null) => {
    setEditingField(field);
    if (field) {
      // 处理字段名映射：如果后端返回了旧的name字段，映射到title_zh和title_en
      const formValues = { ...field };
      if (field.name && !field.title_zh) {
        formValues.title_zh = field.name;
        formValues.title_en = field.name;
      }
      form.setFieldsValue(formValues);
      // 如果是列表类型，加载列表项
      if (field.field_type === 'list') {
        setCurrentFieldId(field.id);
        await loadListItems(field.id);
      } else {
        setCurrentFieldId(null);
        setListItems([]);
      }
    } else {
      form.resetFields();
      setCurrentFieldId(null);
      setListItems([]);
      // 如果有筛选的模块，默认选中
      if (moduleFilter) {
        form.setFieldsValue({ module_id: moduleFilter });
      }
    }
    setModalVisible(true);
  };

  // 关闭弹窗
  const closeModal = () => {
    setModalVisible(false);
    setEditingField(null);
    setCurrentFieldId(null);
    setListItems([]);
    form.resetFields();
  };

  // 打开列表项创建/编辑弹窗
  const openListItemModal = (item = null) => {
    setEditingListItem(item);
    if (item) {
      listItemForm.setFieldsValue({
        ...item,
        detail_kind: item.detail_kind || 'text',
      });
      if (item.cover_image_url) {
        setCoverFileList([
          {
            uid: '-1',
            name: 'cover',
            status: 'done',
            url: resolveAssetUrl(item.cover_image_url),
          },
        ]);
      } else {
        setCoverFileList([]);
      }
    } else {
      listItemForm.resetFields();
      listItemForm.setFieldsValue({ field_id: currentFieldId, detail_kind: 'text' });
      setCoverFileList([]);
    }
    setListItemModalVisible(true);
  };

  // 关闭列表项弹窗
  const closeListItemModal = () => {
    setListItemModalVisible(false);
    setEditingListItem(null);
    setCoverFileList([]);
    listItemForm.resetFields();
  };

  // 提交列表项表单
  const handleListItemSubmit = async () => {
    try {
      const values = await listItemForm.validateFields();
      if (values.detail_kind === 'link') {
        values.content_zh = null;
        values.content_en = null;
      } else {
        values.redirect_url = null;
      }
      if (editingListItem) {
        await listItemsAPI.update(editingListItem.id, values);
        message.success('更新列表项成功');
      } else {
        await listItemsAPI.create(values);
        message.success('创建列表项成功');
      }
      closeListItemModal();
      await loadListItems(currentFieldId);
    } catch (error) {
      if (error.errorFields) {
        return;
      }
      message.error('操作失败: ' + error.message);
    }
  };

  // 删除列表项
  const handleDeleteListItem = async (id) => {
    try {
      await listItemsAPI.delete(id);
      message.success('删除列表项成功');
      await loadListItems(currentFieldId);
    } catch (error) {
      message.error('删除失败: ' + error.message);
    }
  };

  // 提交表单
  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      console.log('提交的表单数据:', values); // 调试用
      // 确保title_zh和title_en字段存在
      if (!values.title_zh || !values.title_en) {
        message.error('请填写中文标题和英文标题');
        return;
      }
      let fieldId;
      if (editingField) {
        const updated = await fieldsAPI.update(editingField.id, values);
        fieldId = updated.id;
        message.success('更新字段成功');
      } else {
        const created = await fieldsAPI.create(values);
        fieldId = created.id;
        message.success('创建字段成功');
      }
      
      // 如果是列表类型，更新当前字段ID并加载列表项
      if (values.field_type === 'list') {
        setCurrentFieldId(fieldId);
        await loadListItems(fieldId);
        // 保持弹窗打开，让用户可以继续管理列表项
      } else {
        setCurrentFieldId(null);
        setListItems([]);
        // 非列表类型，关闭弹窗并刷新列表
        closeModal();
        loadFields();
      }
    } catch (error) {
      if (error.errorFields) {
        // 表单验证错误
        return;
      }
      message.error('操作失败: ' + error.message);
    }
  };

  // 删除字段
  const handleDelete = async (id) => {
    try {
      await fieldsAPI.delete(id);
      message.success('删除字段成功');
      loadFields();
    } catch (error) {
      message.error('删除失败: ' + error.message);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '所属模块',
      dataIndex: 'module_id',
      key: 'module_id',
      width: 150,
      render: (moduleId) => {
        const module = modules.find(m => m.id === moduleId);
        return module ? module.name_zh : moduleId;
      }
    },
    {
      title: '中文标题',
      dataIndex: 'title_zh',
      key: 'title_zh',
      width: 150,
    },
    {
      title: '英文标题',
      dataIndex: 'title_en',
      key: 'title_en',
      width: 150,
    },
    {
      title: '字段类型',
      dataIndex: 'field_type',
      key: 'field_type',
      width: 100,
      render: (type) => {
        const fieldType = FIELD_TYPES.find(t => t.value === type);
        return fieldType ? fieldType.label : type;
      }
    },
    {
      title: '中文内容',
      dataIndex: 'content_zh',
      key: 'content_zh',
      ellipsis: true,
      render: (text, record) => {
        if (record.field_type === 'list') {
          return '列表类型（点击编辑查看列表项）';
        }
        return text || '-';
      }
    },
    {
      title: '英文内容',
      dataIndex: 'content_en',
      key: 'content_en',
      ellipsis: true,
      render: (text, record) => {
        if (record.field_type === 'list') {
          return 'List type (click edit to view items)';
        }
        return text || '-';
      }
    },
    {
      title: '外链URL',
      dataIndex: 'redirect_url',
      key: 'redirect_url',
      width: 150,
      ellipsis: true,
      render: (url) => url || '-'
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Button 
            type="link" 
            icon={<EditOutlined />} 
            onClick={() => openModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除此字段吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <Space>
          <Select
            placeholder="筛选模块"
            style={{ width: 200 }}
            allowClear
            value={moduleFilter}
            onChange={setModuleFilter}
            options={[
              { value: null, label: '全部模块' },
              ...modules.map(m => ({ value: m.id, label: m.name_zh }))
            ]}
          />
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => openModal()}>
          创建字段
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={fields}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1200 }}
      />

      <Modal
        title={editingField ? '编辑字段' : '创建字段'}
        open={modalVisible}
        onOk={handleSubmit}
        onCancel={closeModal}
        width={900}
      >
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="module_id"
                label="所属模块"
                rules={[{ required: true, message: '请选择所属模块' }]}
              >
                <Select placeholder="请选择模块">
                  {modules.map(module => (
                    <Select.Option key={module.id} value={module.id}>
                      {module.name_zh} ({module.page_code})
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="field_type"
                label="字段类型"
                rules={[{ required: true, message: '请选择字段类型' }]}
              >
                <Select placeholder="请选择类型">
                  {FIELD_TYPES.map(type => (
                    <Select.Option key={type.value} value={type.value}>
                      {type.label}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="title_zh"
                label="中文标题"
                rules={[{ required: true, message: '请输入中文标题' }]}
              >
                <Input placeholder="例如：产品经理" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="title_en"
                label="英文标题"
                rules={[{ required: true, message: '请输入英文标题' }]}
              >
                <Input placeholder="例如：Product Manager" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="redirect_url"
            label="外链URL（可选）"
            rules={[
              { type: 'url', message: '请输入有效的URL' }
            ]}
          >
            <Input placeholder="例如：https://example.com/jobs/pm" />
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.field_type !== currentValues.field_type}
          >
            {({ getFieldValue }) => {
              const fieldType = getFieldValue('field_type');
              const fieldId = editingField?.id || currentFieldId;
              
              if (fieldType === 'list') {
                return (
                  <>
                    <Divider>列表项管理</Divider>
                    <div style={{ marginBottom: 16 }}>
                      <Button 
                        type="dashed" 
                        icon={<PlusOutlined />} 
                        onClick={() => openListItemModal()}
                        disabled={!fieldId}
                        block
                      >
                        添加列表项
                      </Button>
                    </div>
                    <Table
                      columns={[
                        {
                          title: '名称',
                          dataIndex: 'name',
                          key: 'name',
                          width: 120,
                          render: (text) => text || '-'
                        },
                        {
                          title: '中文标题',
                          dataIndex: 'title_zh',
                          key: 'title_zh',
                          width: 120,
                          ellipsis: true,
                          render: (text) => text || '-'
                        },
                        {
                          title: '英文标题',
                          dataIndex: 'title_en',
                          key: 'title_en',
                          width: 120,
                          ellipsis: true,
                          render: (text) => text || '-'
                        },
                        {
                          title: '封面',
                          dataIndex: 'cover_image_url',
                          key: 'cover_image_url',
                          width: 72,
                          render: (url) =>
                            url ? (
                              <Image
                                src={resolveAssetUrl(url)}
                                alt=""
                                width={48}
                                height={48}
                                style={{ objectFit: 'cover' }}
                              />
                            ) : (
                              '-'
                            ),
                        },
                        {
                          title: '详情',
                          dataIndex: 'detail_kind',
                          key: 'detail_kind',
                          width: 72,
                          render: (k) => (k === 'link' ? '外链' : '正文'),
                        },
                        {
                          title: '中文内容',
                          dataIndex: 'content_zh',
                          key: 'content_zh',
                          ellipsis: true,
                          render: (text) => text || '-'
                        },
                        {
                          title: '英文内容',
                          dataIndex: 'content_en',
                          key: 'content_en',
                          ellipsis: true,
                          render: (text) => text || '-'
                        },
                        {
                          title: '外链URL',
                          dataIndex: 'redirect_url',
                          key: 'redirect_url',
                          ellipsis: true,
                          render: (url) => url || '-'
                        },
                        {
                          title: '操作',
                          key: 'action',
                          width: 120,
                          render: (_, record) => (
                            <Space>
                              <Button 
                                type="link" 
                                size="small"
                                icon={<EditOutlined />} 
                                onClick={() => openListItemModal(record)}
                              >
                                编辑
                              </Button>
                              <Popconfirm
                                title="确定删除此列表项吗？"
                                onConfirm={() => handleDeleteListItem(record.id)}
                                okText="确定"
                                cancelText="取消"
                              >
                                <Button type="link" danger size="small" icon={<DeleteOutlined />}>
                                  删除
                                </Button>
                              </Popconfirm>
                            </Space>
                          ),
                        },
                      ]}
                      dataSource={listItems}
                      rowKey="id"
                      pagination={false}
                      size="small"
                    />
                  </>
                );
              } else {
                return (
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="content_zh"
                        label="中文内容"
                      >
                        <TextArea 
                          rows={6} 
                          placeholder="请输入中文内容"
                        />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item
                        name="content_en"
                        label="英文内容"
                      >
                        <TextArea 
                          rows={6} 
                          placeholder="Please enter English content"
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                );
              }
            }}
          </Form.Item>
        </Form>
      </Modal>

      {/* 列表项编辑弹窗 */}
      <Modal
        title={editingListItem ? '编辑列表项' : '创建列表项'}
        open={listItemModalVisible}
        onOk={handleListItemSubmit}
        onCancel={closeListItemModal}
        width={800}
      >
        <Form form={listItemForm} layout="vertical">
          <Form.Item name="field_id" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="name"
            label="名称（可选，内部备注）"
          >
            <Input placeholder="可选" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="title_zh" label="中文标题（可选）">
                <Input placeholder="新闻中文标题" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="title_en" label="英文标题（可选）">
                <Input placeholder="News title in English" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="cover_image_url" hidden>
            <Input />
          </Form.Item>
          <Form.Item label="封面图" extra="支持 jpg/png/gif/webp，最大 5MB">
            <Upload
              listType="picture-card"
              maxCount={1}
              fileList={coverFileList}
              customRequest={async ({ file, onSuccess, onError }) => {
                try {
                  const data = await uploadAPI.uploadCover(file);
                  listItemForm.setFieldsValue({ cover_image_url: data.url });
                  setCoverFileList([
                    {
                      uid: file.uid,
                      name: file.name,
                      status: 'done',
                      url: resolveAssetUrl(data.url),
                    },
                  ]);
                  onSuccess(data, file);
                  message.success('上传成功');
                } catch (err) {
                  message.error(err.message || '上传失败');
                  onError(err);
                }
              }}
              onRemove={() => {
                listItemForm.setFieldsValue({ cover_image_url: '' });
                setCoverFileList([]);
              }}
            >
              {coverFileList.length < 1 && (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>上传</div>
                </div>
              )}
            </Upload>
          </Form.Item>
          <Form.Item
            name="detail_kind"
            label="详情类型"
            rules={[{ required: true, message: '请选择详情类型' }]}
          >
            <Radio.Group>
              <Radio value="text">正文（在下方填写中英文内容）</Radio>
              <Radio value="link">外链（跳转至下方链接）</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            noStyle
            shouldUpdate={(prev, cur) => prev.detail_kind !== cur.detail_kind}
          >
            {({ getFieldValue }) =>
              getFieldValue('detail_kind') === 'link' ? (
                <Form.Item
                  name="redirect_url"
                  label="详情链接"
                  dependencies={['detail_kind']}
                  rules={[
                    ({ getFieldValue: get }) => ({
                      validator(_, value) {
                        if (get('detail_kind') !== 'link') return Promise.resolve();
                        if (!value || !String(value).trim()) {
                          return Promise.reject(new Error('请填写详情链接'));
                        }
                        try {
                          // eslint-disable-next-line no-new
                          new URL(value);
                        } catch {
                          return Promise.reject(new Error('请输入有效的 URL'));
                        }
                        return Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <Input placeholder="https://..." />
                </Form.Item>
              ) : (
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item name="content_zh" label="中文正文（详情）">
                      <TextArea rows={6} placeholder="详情中文内容" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item name="content_en" label="英文正文（详情）">
                      <TextArea rows={6} placeholder="Detail in English" />
                    </Form.Item>
                  </Col>
                </Row>
              )
            }
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default FieldManager;



